import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { 
  MessageSquare, 
  Plus, 
  Library, 
  Settings, 
  Store, 
  Search, 
  Star, 
  FileText, 
  Clock,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Users,
  Globe,
  Edit,
  Heart,
  LayoutDashboard,
  Crown,
  Mail,
  TestTube,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSidebarNavigation } from '@/hooks/navigation/useSidebarNavigation';
import { SidebarSection, NavigationItem } from '@/types/navigation';
import NavigationItemComponent from './NavigationItem';
import HistorySection from './HistorySection';
import UserMenu from './UserMenu';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';
import FilePreviewModal from '@/components/integrations/FilePreviewModal';
import PositPlusLogo from '@/components/shared/PositPlusLogo';
import { useSubscription } from '@/hooks/useSubscription';
import { useIsMobile } from '@/hooks/use-mobile';
import { STRIPE_PRICE_IDS } from '@/constants/pricing';

const SidebarNav: React.FC = () => {
  // Only Chat History is collapsible
  const [expandedSections, setExpandedSections] = useState(new Set<string>());
  const navigation = useSidebarNavigation();
  const { user, signOut, isLoading: isLoadingAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentPosit, sidebarOpen, toggleSidebar } = useChatInterface();
  const { handleSubscribe } = useSubscription();
  const isMobile = useIsMobile();
  
  // Determine if we're in edit mode
  const isEditMode = location.pathname.includes('/update-posit/');
  const isCreateMode = location.pathname.includes('/create-posit');

  // Helper function to handle navigation with auto-close on mobile
  const handleNavigationClick = (path: string) => {
    navigate(path);
    // Auto-close sidebar on mobile after navigation
    if (isMobile && sidebarOpen) {
      toggleSidebar();
    }
  };

  // Get user subscription status
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["user-profile-sidebar", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("users")
        .select("subscription_type, trial_end_date")
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      const isTrialValid = data?.trial_end_date ? new Date(data.trial_end_date) > new Date() : false;
      const isPremiumOrTrial = data?.subscription_type === 'posit_plus' || 
                              (data?.subscription_type === 'trial' && isTrialValid);
      
      return {
        ...data,
        isPremium: isPremiumOrTrial
      };
    },
    enabled: !!user?.id
  });

  const isPremium = userProfile?.isPremium || false;

  // Handle upgrade button click
  const handleUpgrade = async () => {
    await handleSubscribe(STRIPE_PRICE_IDS.MONTHLY);
    // Auto-close sidebar on mobile after upgrade action
    if (isMobile && sidebarOpen) {
      toggleSidebar();
    }
  };
  
  // Query for user's posits (up to 5 for simple list)
  const { data: userPosits = [] } = useQuery({
    queryKey: ['sidebar-user-posits', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('posits')
        .select('id, title, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(5);

      if (error) {
        return [];
      }

      return data.map(posit => ({
        id: posit.id,
        label: posit.title,
        href: `/update/${posit.id}`,
        isActive: location.pathname === `/update/${posit.id}`
      } as NavigationItem));
    },
    enabled: !!user?.id
  });

  // Query for saved posits (up to 5 for simple list)
  const { data: savedPosits = [] } = useQuery({
    queryKey: ['sidebar-saved-posits', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('saved_posits')
        .select(`
          id,
          posit_id,
          posits!inner (
            id,
            title,
            users!fk_posits_user_id (
              first_name,
              last_name,
              handle
            )
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        return [];
      }

      return data.map(saved => ({
        id: `saved-${saved.posit_id}`,
        label: saved.posits.title,
        href: `/chat/${saved.posit_id}`,
        isActive: location.pathname === `/chat/${saved.posit_id}`
      } as NavigationItem));
    },
    enabled: !!user?.id
  });
  
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(sectionId)) {
        newExpanded.delete(sectionId);
      } else {
        newExpanded.add(sectionId);
      }
      return newExpanded;
    });
  };

  const handleNewChat = () => {
    if (isEditMode || isCreateMode) {
      // In edit mode, go to posits dashboard
      handleNavigationClick('/dashboard/posits');
    } else {
      // In chat mode, start new chat
      navigation.startNewChat();
      // If we're on a chat page, reload to start fresh
      if (window.location.pathname.includes('/chat/')) {
        window.location.reload();
      }
      // Auto-close sidebar on mobile after starting new chat
      if (isMobile && sidebarOpen) {
        toggleSidebar();
      }
    }
  };

  // Check if user is admin using the proper RPC function
  const { data: isAdmin } = useQuery({
    queryKey: ["user-admin-role-sidebar", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data, error } = await supabase
        .rpc('has_role', { 
          user_id: user.id,
          role: 'admin'
        });
      if (error) {
        return false;
      }
      return data;
    },
    enabled: !!user
  });

  return (
    <>
    <nav className="flex-1 space-y-4 px-3">
      {/* User Profile at Top */}
      <div className="py-2">
        <UserMenu />
      </div>
      
      {/* Upgrade to Posit Plus - Show at top if user is not subscribed */}
      {!isLoadingAuth && !isLoadingProfile && !isPremium && (
        <div className="px-2 py-2">
          <Button 
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-br from-primary via-primary-hover to-[#D946EF] text-white hover:opacity-90 transition-opacity text-sm font-medium py-2.5"
          >
            <Crown className="h-4 w-4 mr-2" />
            Upgrade to Posit Plus
          </Button>
        </div>
      )}
      
      {/* Dashboard Section */}
      <div className="space-y-1">
        <div className="space-y-0.5">
          <NavigationItemComponent
            item={{ 
              id: 'dashboard-overview', 
              label: 'Dashboard', 
              icon: LayoutDashboard, 
              href: '/dashboard',
              isActive: location.pathname === '/dashboard'
            }}
            onClick={() => handleNavigationClick('/dashboard')}
          />
          <NavigationItemComponent
            item={{ 
              id: 'dashboard-posits', 
              label: 'Posits', 
              icon: Library, 
              href: '/dashboard/posits',
              isActive: location.pathname === '/dashboard/posits'
            }}
            onClick={() => handleNavigationClick('/dashboard/posits')}
          />
          <NavigationItemComponent
            item={{ 
              id: 'dashboard-create', 
              label: 'Create', 
              icon: Plus, 
              href: '/dashboard/create',
              isActive: location.pathname === '/dashboard/create'
            }}
            onClick={() => handleNavigationClick('/dashboard/create')}
          />

          <NavigationItemComponent
            item={{ 
              id: 'browse-posits', 
              label: 'Explore Posits', 
              icon: Globe, 
              href: '/public-posits',
              isActive: location.pathname === '/public-posits'
            }}
            onClick={() => handleNavigationClick('/public-posits')}
          />
          <NavigationItemComponent
            item={{ 
              id: 'my-subscriptions', 
              label: 'Saved Posits', 
              icon: Star, 
              href: '/dashboard/subscriptions',
              isActive: location.pathname === '/dashboard/subscriptions'
            }}
            onClick={() => handleNavigationClick('/dashboard/subscriptions')}
          />
        </div>
      </div>

      {/* Chat History - Collapsible */}
      <div className="space-y-0.5">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleSection('chat-history')}
            className="w-full justify-start gap-2 px-2 py-1.5 h-auto font-medium text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50"
          >
            <Clock className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1 text-left">Chat History</span>
            {expandedSections.has('chat-history') ? 
              <ChevronDown className="h-3 w-3" /> : 
              <ChevronRight className="h-3 w-3" />
            }
          </Button>
        </div>
        {expandedSections.has('chat-history') && (
          <div className="ml-4 space-y-0.5">
            <HistorySection 
              chatHistory={navigation.filteredChatHistory}
              onSelectConversation={navigation.selectConversation}
              historyFilter={navigation.historyFilter}
              onFilterChange={navigation.setHistoryFilter}
            />
          </div>
        )}
      </div>

      {/* Bottom Tools Section */}
      <div className="space-y-1">
        <NavigationItemComponent
          item={{ 
            id: 'dashboard-analytics', 
            label: 'Analytics', 
            icon: BarChart3, 
            href: '/dashboard/analytics',
            isActive: location.pathname === '/dashboard/analytics',
            isPremium: true,
            hasAccess: isPremium
          }}
          onClick={() => {
            if (isPremium) {
              handleNavigationClick('/dashboard/analytics');
            } else {
              handleUpgrade();
            }
          }}
        />
        <NavigationItemComponent
          item={{ 
            id: 'dashboard-integrations', 
            label: 'Integrations', 
            icon: ExternalLink, 
            href: '/dashboard/integrations',
            isActive: location.pathname === '/dashboard/integrations'
          }}
          onClick={() => handleNavigationClick('/dashboard/integrations')}
        />
        <NavigationItemComponent
          item={{ 
            id: 'dashboard-affiliate', 
            label: 'Affiliate Program', 
            icon: Users, 
            href: '/dashboard/affiliate',
            isActive: location.pathname === '/dashboard/affiliate',
            isPremium: true,
            hasAccess: isPremium
          }}
          onClick={() => {
            if (isPremium) {
              handleNavigationClick('/dashboard/affiliate');
            } else {
              handleUpgrade();
            }
          }}
        />
        <NavigationItemComponent
          item={{ 
            id: 'settings', 
            label: 'Settings', 
            icon: Settings, 
            href: '/dashboard/settings',
            isActive: location.pathname === '/dashboard/settings' || location.pathname === '/settings'
          }}
          onClick={() => handleNavigationClick('/dashboard/settings')}
        />
        <NavigationItemComponent
          item={{ 
            id: 'feature-requests', 
            label: 'Feature Requests', 
            icon: FileText, 
            href: '/feature-requests',
            isActive: location.pathname === '/feature-requests'
          }}
          onClick={() => handleNavigationClick('/feature-requests')}
        />
        <NavigationItemComponent
          item={{ 
            id: 'report-bug', 
            label: 'Report a Bug', 
            icon: Mail, 
            href: '/bug-reports',
            isActive: location.pathname === '/bug-reports'
          }}
          onClick={() => handleNavigationClick('/bug-reports')}
        />
      </div>

      {/* Admin Section - Only show for admin users */}
      {isAdmin && (
        <div className="space-y-1 pt-2">
          <div className="px-2 py-1">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Admin
            </h3>
          </div>
          <div className="space-y-0.5">
            <NavigationItemComponent
              item={{ 
                id: 'admin-business', 
                label: 'Business Dashboard', 
                icon: BarChart3, 
                href: '/admin/business',
                isActive: location.pathname.startsWith('/admin/business')
              }}
              onClick={() => handleNavigationClick('/admin/business')}
            />
            <NavigationItemComponent
              item={{ 
                id: 'admin-health', 
                label: 'Health Dashboard', 
                icon: MessageSquare, 
                href: '/admin/health',
                isActive: location.pathname.startsWith('/admin/health')
              }}
              onClick={() => handleNavigationClick('/admin/health')}
            />
            <NavigationItemComponent
              item={{ 
                id: 'admin-posit-performance', 
                label: 'Posit Performance Test', 
                icon: TestTube, 
                href: '/admin/health/batch-testing',
                isActive: location.pathname.startsWith('/admin/health/batch-testing')
              }}
              onClick={() => handleNavigationClick('/admin/health/batch-testing')}
            />
          </div>
        </div>
      )}
    </nav>

    {/* File Preview Modal */}
    <FilePreviewModal
      file={navigation.selectedFile}
      isOpen={navigation.showFilePreview}
      onClose={navigation.closeFilePreview}
    />
    </>
  );
};

export default SidebarNav; 